import type { PdfInspection } from "@estada/plan-analyzer";
import { tagFromName } from "../../../src/models/tagFromName";
import getFloorOptionsList from "../../../src/components/Quickstart/FloorOptionsList";

type FloorChoice = { id: string; tag: string; name: string };
export type PdfImportRowDefault = { selected: boolean; floorName: string };

/** Only valid manual entries become the shared default; individual choices stay fixed. */
export function updatePdfImportScale<T extends { scale: string; scaleExplicit: boolean }>(
  rows: T[], index: number, scale: string,
): T[] {
  const value = Number(scale);
  const valid = Number.isFinite(value) && value >= 1 && value <= 10000;
  return rows.map((row, i) => i === index
    ? { ...row, scale, scaleExplicit: true }
    : valid && !row.scaleExplicit ? { ...row, scale } : row);
}
/** Pure preselection shared by the dialog and its regression tests. */
export function createPdfImportRows(
  inspection: PdfInspection,
  floors: FloorChoice[],
  currentFloorId: string,
  maxTagCharacters = 5,
  defaults?: readonly PdfImportRowDefault[],
) {
  const hasNames = inspection.pages.some((page) => page.pageName || (page.pageKind && page.pageKind !== "unknown"));
  const normalize = (name: string) => name.normalize("NFKC").trim().toLocaleLowerCase();
  const cleanFloorName = (name: string) =>
    name
      .replace(/\bgrundriss\b/gi, "")
      .replace(/^\s*[:\-–]+\s*|\s*[:\-–]+\s*$/g, "")
      .replace(/\s+/g, " ")
      .trim();
  const usedFloors = new Set<string>();
  const floorNames = getFloorOptionsList();
  const canonicalName = (name: string) => {
    let result = name;
    for (const floorName of floorNames) {
      const pattern = floorName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/geschoss/gi, "gescho(?:ss|ß)");
      result = result.replace(new RegExp(`(?<![\\p{L}])${pattern}(?![\\p{L}])`, "giu"), () => floorName);
    }
    return result;
  };
  const reservedTags = floors.map((floor) => floor.tag.toLocaleUpperCase());
  return inspection.pages.map((page, index) => {
    const detectedName = cleanFloorName(page.storeyName || page.pageName || "");
    const matchingFloors =
      page.isFloorPlan && detectedName
        ? floors.filter(
            (floor) =>
              normalize(floor.name) === normalize(detectedName) || normalize(floor.tag) === normalize(detectedName),
          )
        : [];
    const floor = hasNames
      ? matchingFloors.length === 1 && !usedFloors.has(matchingFloors[0].id)
        ? matchingFloors[0]
        : undefined
      : index === 0
        ? floors.find((floor) => floor.id === currentFloorId)
        : undefined;
    if (floor) usedFloors.add(floor.id);
    const floorName =
      defaults?.[index]?.floorName || floor?.name || canonicalName(detectedName || cleanFloorName(page.label || "")) || "PDF-Seite " + page.page;
    const tag = floor?.tag || tagFromName(floorName, maxTagCharacters, reservedTags);
    reservedTags.push(tag);
    // A Dachgeschoss is an importable storey; roof drawings still remain excluded.
    const exclusionName = [page.storeyName, page.pageName, floorName]
      .join(" ")
      .replace(/\bdach[\s-]*gescho(?:ss|ß)(?![\p{L}\p{N}])/giu, "");
    const excludedFromDefault = /dach|fundament/i.test(exclusionName);
    return {
      page: page.page,
      selected: defaults?.[index]?.selected ?? (!excludedFromDefault && (hasNames ? page.isFloorPlan === true : index === 0)),
      scale: String(page.printedScale ?? page.suggestedScale ?? ""),
      scaleExplicit: Boolean(page.printedScale),
      floorTag: tag,
      floorName,
      floorId: floor?.id || "",
    };
  });
}
